import {
  LeftOutlined,
  SearchOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Button, Card, Input, Table, message } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import useAxios from "../../hooks/useAxios";

const InactiveAmeyoLeads = ({
  setIsInactiveAmeyoOpen,
  dateRange = [],
  fetchInactiveCount,
}) => {
  const history = useHistory();
  const api = useAxios();
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [search, setSearch] = useState("");
  const searchParams = new URLSearchParams(history.location.search);
  const pageFromUrl = parseInt(searchParams.get("page"), 10) || 1;
  const [page, setPage] = useState(pageFromUrl);
  const [pageSize, setPageSize] = useState(10);
  const [candidates, setCandidates] = useState({
    records: [],
    totalRecords: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const getInactiveLeads = async (pageNo = page, size = pageSize, searchQuery = search) => {
    try {
      setLoading(true);
      const params = {
        FormId: "[]",
        PageSize: size,
        PageNo: pageNo,
        Search: searchQuery || "",
        Position: "[]",
        ProcessId: "",
        Status: "",
        StartDate: dateRange?.[0] || "",
        EndDate: dateRange?.[1] || "",
        ScoreType: "",
        InterviewType: "Application_Received",
        TabType: "Application_Received",
        isServiceActive: "false",
      };

      const { data } = await api.get("/candidates/Pagination", {
        params,
        tokenType: "user",
      });

      const candidateList = data?.candidates || data?.candidateList || [];
      const totalCount = data?.candidateCount || data?.totalRecords || 0;

      const rows = candidateList.map((item) => {
        let qualification = item?.personalDetails?.["Highest Qualification"] || "";
        let experience = item?.personalDetails?.["Total Experience"] || "";

        item?.responses?.forEach((response) => {
          if (response?.label === "Highest Qualification") {
            qualification = response?.value || qualification;
          }
          if (response?.label === "Total Experience") {
            experience = response?.value || experience;
          }
        });

        let displayDate = "-";
        if (item?.dateApplied) {
          displayDate = item.dateApplied.split(" ")[0] || item.dateApplied;
        } else if (item?.createdAt) {
          displayDate = new Date(item.createdAt).toLocaleDateString("en-GB");
        }

        return {
          key: item?._id || item?.id,
          candidate: item,
          date: displayDate,
          phone: item?.["Phone Number"] || item?.mobileNo || item?.phone || "-",
          name: item?.Name || item?.name || item?.personalDetails?.Name || "-",
          location: item?.location || item?.personalDetails?.["Preferred Location"] || "-",
          qualification: qualification || "-",
          experience: experience || "-",
        };
      });

      setCandidates({ records: rows, totalRecords: totalCount });
    } catch (err) {
      console.error("Error fetching inactive Ameyo leads:", err);
      message.error("Failed to load inactive leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInactiveLeads(page, pageSize, search);
  }, [page, pageSize, dateRange]);

  const initialSearch = useRef(true);
  useEffect(() => {
    if (initialSearch.current) {
      initialSearch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      getInactiveLeads(1, pageSize, search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const handlePushToAmeyo = async () => {
    if (!selectedRows || selectedRows.length === 0) {
      message.warning("Please select at least one candidate to push.");
      return;
    }

    const mobileNumbers = selectedRows
      .map((row) => row.phone || row.candidate?.mobileNo)
      .filter((phone) => phone && phone !== "-" && phone.trim() !== "");

    if (mobileNumbers.length === 0) {
      message.warning("None of the selected candidates have a valid phone number.");
      return;
    }

    try {
      setPushing(true);
      const res = await api.post(
        "/ameyoDialer/push-contacts",
        { mobileNumbers },
        { tokenType: "user" }
      );

      if (res?.data?.success) {
        message.success(res.data.message || `Successfully pushed ${mobileNumbers.length} contact(s) to Ameyo.`);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        getInactiveLeads(page, pageSize, search);
        if (typeof fetchInactiveCount === "function") {
          fetchInactiveCount();
        }
      } else {
        message.error(res?.data?.message || "Failed to push contacts to Ameyo.");
      }
    } catch (err) {
      console.error("Push to Ameyo error:", err);
      const errorMsg = err.response?.data?.message || err.message || "An error occurred while pushing to Ameyo.";
      message.error(errorMsg);
    } finally {
      setPushing(false);
    }
  };

  const columns = [
    {
      title: "DATE",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span
          onClick={() =>
            history.push({
              pathname: `/candidate/${record.key}`,
              state: {
                from: `/manager/dashboard?isServiceActive=false&page=${page}`,
                fromName: "Dashboard",
                isInactiveAmeyo: true,
              },
            })
          }
          className="cursor-pointer hover:underline font-medium"
        >
          {text}
        </span>
      ),
    },
    {
      title: "PHONE NUMBER",
      dataIndex: "phone",
      key: "phone",
      render: (text, record) => (
        <span
          onClick={() =>
            history.push({
              pathname: `/candidate/${record.key}`,
              state: {
                from: `/manager/dashboard?isServiceActive=false&page=${page}`,
                fromName: "Dashboard",
                isInactiveAmeyo: true,
              },
            })
          }
          className="cursor-pointer hover:underline"
        >
          {text}
        </span>
      ),
    },
    {
      title: "LOCATION",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "QUALIFICATION",
      dataIndex: "qualification",
      key: "qualification",
    },
    {
      title: "EXPERIENCE",
      dataIndex: "experience",
      key: "experience",
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
      setSelectedRows((prevSelectedRows) => {
        const rowMap = new Map((prevSelectedRows || []).map((r) => [r.key, r]));
        const currentPageKeys = new Set((candidates?.records || []).map((r) => r.key));

        (candidates?.records || []).forEach((row) => {
          if (keys.includes(row.key)) {
            rowMap.set(row.key, row);
          } else if (currentPageKeys.has(row.key)) {
            rowMap.delete(row.key);
          }
        });

        const keysSet = new Set(keys);
        return Array.from(rowMap.values()).filter((row) => keysSet.has(row.key));
      });
    },
  };

  return (
    <Card>
      <div className="py-1 px-1 mb-2 flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-[10px]">
          <button
            onClick={() => setIsInactiveAmeyoOpen(false)}
            className="flex items-center mt-[2px] outline-none border-none bg-transparent cursor-pointer"
          >
            <LeftOutlined className="!text-[18px]" />
          </button>
          <h4 className="text-[1.5rem] mb-0 font-semibold">Ameyo Inactive Leads</h4>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={pushing}
            disabled={selectedRowKeys.length === 0}
            onClick={handlePushToAmeyo}
            className={`${
              selectedRowKeys.length > 0
                ? "!bg-orange-500 hover:!bg-orange-600 !border-orange-500 !text-white"
                : "!bg-orange-300 !border-orange-300 !text-white cursor-not-allowed opacity-75"
            } font-medium flex items-center shadow-none`}
            style={{ color: "#ffffff" }}
          >
            Push to Ameyo {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-[1.5rem] mt-4">
        <div className="flex justify-between items-center">
          <div className="flex-grow">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                borderRadius: "8px",
                width: "100%",
                maxWidth: "300px",
              }}
              size="large"
              placeholder="Search leads..."
              prefix={<SearchOutlined />}
            />
          </div>
        </div>
        <div>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={candidates?.records}
            loading={loading}
            pagination={{
              total: candidates?.totalRecords || 0,
              pageSizeOptions: ["5", "10", "30", "50"],
              showSizeChanger: true,
              current: page,
              pageSize: pageSize,
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                history.replace(`/manager/dashboard?isServiceActive=false&page=${newPage}`);
                if (newPageSize !== pageSize) {
                  setPageSize(newPageSize);
                  setPage(1);
                  history.replace(`/manager/dashboard?isServiceActive=false&page=1`);
                }
              },
            }}
            showSorterTooltip={false}
            scroll={{ x: true }}
            style={{ whiteSpace: "pre" }}
            size="small"
          />
        </div>
      </div>
    </Card>
  );
};

export default InactiveAmeyoLeads;
